import React, { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Navbar6 from "../components/Navbar6";
import { XCircle } from "lucide-react";
import RevisionNotes from "../components/RevisionNotes";
import DesktopWarningBanner from "../components/DesktopWarningBanner";

// Add scrollbar styles injection function
const injectScrollbarStyles = (htmlContent) => {
  const scrollbarStyles = `
    <style>
      ::-webkit-scrollbar {
        width: 6px;
      }
      
      ::-webkit-scrollbar-track {
        background: transparent;
      }
      
      ::-webkit-scrollbar-thumb {
        background-color: rgba(100, 100, 100, 0.5);
        border-radius: 4px;
      }
      
      ::-webkit-scrollbar-thumb:hover {
        background-color: rgba(100, 100, 100, 0.7);
      }
      
      * {
        scrollbar-width: thin;
        scrollbar-color: rgba(100, 100, 100, 0.5) transparent;
      }
    </style>
  `;

  if (htmlContent.includes('<head>')) {
    return htmlContent.replace('<head>', '<head>' + scrollbarStyles);
  } else {
    return htmlContent.replace('<html>', '<html><head>' + scrollbarStyles + '</head>');
  }
};

/* ---------- rate‑limit reporter (identical to ViewDetails) ---------- */
const injectRateLimitReporter = (htmlContent) => {
  const reporterScript = `
    <script>
      (function(){
        const API_MARKER = '/api/v1/images/';
        window.addEventListener('error', function(e){
          const img = e.target;
          if (img.tagName === 'IMG' && img.src.includes(API_MARKER)) {
            fetch(img.src, { method: 'HEAD', cache: 'no-store' })
              .then(r => {
                if (r.status === 429) {
                  parent.postMessage(
                    { type: 'IMAGE_RATE_LIMIT', src: img.src },
                    '*'
                  );
                }
              })
              .catch(()=>{});
          }
        }, true);
      })();
    </script>
  `;
  return htmlContent.includes('</body>')
    ? htmlContent.replace('</body>', reporterScript + '</body>')
    : htmlContent + reporterScript;
};

// Gemini-specific tokenizer function
const estimateGeminiTokens = (text) => {
  if (!text) return 0;
  
  // Gemini tends to be more character-based in its tokenization
  // For HTML specifically, we need some heuristics
  
  // Remove comments to not count them in the token estimation
  const withoutComments = text.replace(/<!--[\s\S]*?-->/g, '');
  
  // Count HTML tags separately - they tokenize differently
  const htmlTags = withoutComments.match(/<[^>]+>/g) || [];
  const htmlTagsText = htmlTags.join('');
  
  // Content without tags
  const contentText = withoutComments.replace(/<[^>]+>/g, ' ').trim();
  
  // Estimate tokens for different parts:
  // 1. HTML tags (Gemini tends to be more efficient with HTML)
  const tagTokens = Math.ceil(htmlTagsText.length * 0.2);
  
  // 2. Content tokens - Gemini uses more character-based tokenization
  // Calculate character counts for different content types
  const wordCharCount = contentText.length;
  
  // 3. Count common programming tokens in <script> tags
  const scriptContent = (text.match(/<script[^>]*>([\s\S]*?)<\/script>/g) || [])
    .map(script => script.replace(/<\/?script[^>]*>/g, ''))
    .join(' ');
  
  const scriptTokens = Math.ceil(scriptContent.length * 0.25); // Script content is more token-efficient
  
  // 4. Count style tags
  const styleContent = (text.match(/<style[^>]*>([\s\S]*?)<\/style>/g) || [])
    .map(style => style.replace(/<\/?style[^>]*>/g, ''))
    .join(' ');
  
  const styleTokens = Math.ceil(styleContent.length * 0.25); // CSS is also more token-efficient
  
  // 5. Calculate basic content tokens
  // Gemini is more efficient with natural language but less with specialized formats
  const contentTokens = Math.ceil(wordCharCount * 0.3);
  
  // Sum all token estimates and add a safety margin
  const totalTokens = Math.ceil((tagTokens + contentTokens + scriptTokens + styleTokens) * 1.1);
  
  return totalTokens;
};

function ReviseView() {
  const location = useLocation();
  const navigate = useNavigate();
  const [showTokenWarning, setShowTokenWarning] = useState(false);
  const warningShownRef = useRef(false);

  // Extract data passed from the previous page (including currentIndex)
  const { projectId, view, currentIndex } = location.state || {};

  // Check token count - placed before any conditionals
  useEffect(() => {
    if (view && view.versions && view.versions[currentIndex]) {
      const html = view.versions[currentIndex].html;
      if (html && !warningShownRef.current) {
        // Use our Gemini-specific tokenizer function
        const estimatedTokens = estimateGeminiTokens(html);
        
        if (estimatedTokens > 14500) {
          setShowTokenWarning(true);
          warningShownRef.current = true;
        }
        
        // Log token count for debugging
        console.log(`Estimated Gemini token count: ${estimatedTokens}`);
      }
    }
  }, [view, currentIndex]);

  // Close handler to navigate back to view-details
  const handleClose = () => {
    navigate("/view-details", {
      state: {
        projectId,
        viewId: view?._id
      }
    });
  };

  if (!projectId || !view || !view.versions) {
    return (
      <div className="min-h-screen flex items-center justify-center text-red-500">
        Error: Missing required data.
      </div>
    );
  }

  // Determine device type (Desktop or Mobile)
  const device = view.device || "Desktop";

  // Retrieve the correct version from the array
  const currentVersion = view.versions[currentIndex];

  if (!currentVersion) {
    return (
      <div className="min-h-screen flex items-center justify-center text-red-500">
        No version found.
      </div>
    );
  }

  const versionId = currentVersion.versionId;

  // Decide container width based on the device type
  const containerClasses =
    device === "Mobile"
      ? "border rounded-md shadow-sm p-4 bg-white mx-auto max-w-sm"
      : "border rounded-md shadow-sm p-4 bg-white w-full";

  return (
    <div className="relative min-h-screen bg-gray-50">
      <DesktopWarningBanner isDesktop={device === "Desktop"} />
      <Navbar6 pageTitle={view.title} onClose={handleClose} />

      {/* ONLY CHANGE: Mobile vertical layout, Desktop horizontal layout */}
      <div className="max-w-7xl mx-auto p-4 flex flex-col md:flex-row md:space-x-4 space-y-4 md:space-y-0">
        {/* HTML preview area - Full width on mobile, 2/3 width on desktop */}
        <div className="w-full md:w-2/3 relative">
          <div
            className={`${containerClasses} overflow-y-auto`}
            style={{ height: "80vh" }}
          >
            <iframe
              title="Embedded View (Revise)"
              srcDoc={
                injectRateLimitReporter(
                injectScrollbarStyles(currentVersion.html))}
              style={{ width: "100%", height: "100%", border: "none" }}
              sandbox="allow-scripts allow-same-origin allow-popups"
            />
          </div>
        </div>

        {/* Revision notes area - Full width on mobile, 1/3 width on desktop */}
        <div className="w-full md:w-1/3">
          {showTokenWarning && (
            <div className="bg-yellow-50 border-l-4 border-yellow-400 p-3 mb-4 shadow-sm rounded flex justify-between items-center">
              <p className="text-sm text-yellow-800">
                File size is approaching AI service limits. Revision quality may be affected.
              </p>
              <button
                onClick={() => setShowTokenWarning(false)}
                className="text-yellow-500 hover:text-yellow-600"
              >
                <XCircle className="h-5 w-5" />
              </button>
            </div>
          )}
          <RevisionNotes
            projectId={projectId}
            viewId={view._id}
            versionId={versionId}
          />
        </div>
      </div>
    </div>
  );
}

export default ReviseView;